import { ReactElement, useEffect } from 'react';
import { Box, DialogContentText, MenuItem, Stack, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import { LocalStackInstance } from '@localstack/types';
import { VALIDATION_RULES } from '@localstack/services';
import { useForm } from 'react-hook-form';

import { ButtonWithDialog, ContentModalActions } from '../../feedback';
import { ControlledTextField } from '../../form';
import { BaseHeaderActionsMenu } from '../../navigation';

type InstanceActionsMenuProps = {
  isInstanceRunning: boolean;
  isEphemeralInstance: boolean;
  isInstanceRestarting: boolean;
  isDefaultInstance: boolean;
  instance: LocalStackInstance;
  restartInstance: () => void;
  clearAllState: () => void;
  onInstanceRemove?: (instance: LocalStackInstance) => void;
  onInstanceEdit?: (instance: LocalStackInstance) => void;
};

type EditInstanceFormFields = { endpoint: string };

export const InstanceActionsMenu = ({
  isInstanceRunning,
  isEphemeralInstance,
  isDefaultInstance,
  isInstanceRestarting,
  instance,
  onInstanceRemove,
  onInstanceEdit,
  restartInstance,
  clearAllState,
}: InstanceActionsMenuProps): ReactElement => {
  const isRemovableInstance = !isDefaultInstance;

  const { control, handleSubmit, reset } = useForm<EditInstanceFormFields>({
    mode: 'all',
  });

  const submitHandler = ({ endpoint }: EditInstanceFormFields, closeDialog: () => void) => {
    const updatedInstance: LocalStackInstance = { ...instance, endpoint };
    onInstanceEdit?.(updatedInstance);
    closeDialog();
  };

  useEffect(() => {
    reset({ endpoint: instance.endpoint });
  }, [instance.endpoint]);

  return (
    <BaseHeaderActionsMenu>
      {!isEphemeralInstance && (
        <ButtonWithDialog
          componentType="MenuItem"
          dialogTitle="Edit Stack"
          hideDialogActions
          dialogContent={(closeDialog) => (
            <form onSubmit={handleSubmit((data) => submitHandler(data, closeDialog))}>
              <Stack spacing={2} pt={1}>
                <ControlledTextField
                  control={control}
                  required
                  name="endpoint"
                  label="Endpoint URL"
                  placeholder="Endpoint URL"
                  fullWidth
                  variant="outlined"
                  rules={VALIDATION_RULES.required}
                />
              </Stack>
              <Box pt={2}>
                <ContentModalActions
                  onClose={closeDialog}
                  confirmButtonText="Update"
                  confirmButtonProps={{
                    type: 'submit',
                  }}
                />
              </Box>
            </form>
          )}
        >
          Edit Stack
        </ButtonWithDialog>
      )}
      <ButtonWithDialog
        componentType="MenuItem"
        dialogTitle="Remove instance"
        dialogContent={`Are you sure you want to remove the instance called ${instance.endpoint}?`}
        color="primary"
        onClick={() => instance && onInstanceRemove?.(instance)}
        disabled={!isRemovableInstance}
      >
        <Box display="flex" flexDirection="column">
          <Typography variant="inherit">{isEphemeralInstance ? 'Shut down' : 'Remove Stack'}</Typography>
          {isDefaultInstance && (
            <Typography lineHeight="inherit" fontSize={12}>
              Cannot delete default stack
            </Typography>
          )}
        </Box>
      </ButtonWithDialog>
      <ButtonWithDialog
        componentType="MenuItem"
        dialogTitle="Restart instance"
        dialogContent={
          <DialogContentText>
            Are you sure you want to restart this instance? <br />
            Restarting will reset the instance state unless persistence is enabled.
          </DialogContentText>
        }
        color="primary"
        onClick={() => {
          restartInstance();
          clearAllState();
        }}
        disabled={!isInstanceRunning || isInstanceRestarting}
        confirmButtonText="Restart Instance"
      >
        <Box display="flex" flexDirection="column">
          <Typography variant="inherit">Restart</Typography>
          {!isInstanceRunning && (
            <Typography lineHeight="inherit" fontSize={12}>
              Stack is not running
            </Typography>
          )}
        </Box>
      </ButtonWithDialog>
      {!isEphemeralInstance && (
        <>
          <Divider />
          <MenuItem
            component="a"
            href={`${instance.endpoint}/_localstack/swagger`}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!isInstanceRunning || isInstanceRestarting}
          >
            <Box display="flex" flexDirection="column">
              <Typography variant="inherit">API Docs</Typography>
              {!isInstanceRunning && (
                <Typography lineHeight="inherit" fontSize={12}>
                  Stack is not running
                </Typography>
              )}
            </Box>
          </MenuItem>
        </>
      )}
    </BaseHeaderActionsMenu>
  );
};
